/*
 * Copyright (c) 2025 Bolmate B.V.
 */
import './style/emailAdminInfo.scss';

import {AwesomeIcon, CloseButton, Link, Loader} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import classNames from 'classnames';
import React, {MouseEventHandler, useState} from 'react';
import EmailStep from '../../../../models/emailStepModel';
import {DUTCH_DATE_TIME_FORMAT} from '../../../../util/constants';
import {EmailActivity, EmailFlow, EmailStepAction} from '../../../../util/interfaces';
import {ProductSelectionHelper} from '../../../../util/productSelectionHelper';

interface Props {
    emailStep: EmailStep | null;
    open: boolean;
    loading: boolean;
    onClose: MouseEventHandler;
    onActivate: MouseEventHandler;
}

export default function EmailAdminInfo(props: Props) {
    const {emailStep, open, loading, onClose, onActivate} = props;
    const [expandActivity, setExpandActivity] = useState(false);

    function onToggleExpand() {
        setExpandActivity(!expandActivity);
    }

    function renderField(label: string, value: string | number | undefined) {
        return (
            <div className='field'>
                <label className='label'>{label}</label>
                <label>{value ?? '-'}</label>
            </div>
        );
    }

    function renderProducts() {
        if (!emailStep) return null;
        const label = 'Producten';
        const helper = new ProductSelectionHelper(emailStep.productSelectionJSON.clauses);
        if(helper.isAllSelected()) return renderField(label, 'Alle');
        else if (helper.isRemainingSelected()) return renderField(label, 'Resterende');
        return renderField(label, 0);
    }

    function renderEmailType() {
        let value: string = '';
        if (emailStep?.flow === EmailFlow.INVOICE) value = 'Factuurmail';
        else if (emailStep?.flow === EmailFlow.REVIEW_INVOICE) value = 'Review + factuurmail';
        return renderField('Soort mail', value);
    }

    function renderActivity() {
        if (!emailStep) return null;
        const {activity} = emailStep;
        const minRender = 5;
        const renderActivity = expandActivity ? activity : activity.slice(0, minRender);
        const toggleLabel = expandActivity ? 'Minder tonen' : 'Alles tonen';
        return (
            <div className='activity-container'>
                <label className='activity-title'>Activiteit</label>
                {renderActivity.map(renderActivityItem)}
                {activity.length > minRender && <Link onClick={onToggleExpand}>{toggleLabel}</Link>}
            </div>
        );
    }

    function renderActivityItem(item: EmailActivity, index: number) {
        return (
            <div className='activity-item' key={index}>
                <AwesomeIcon name='power-off' className={classNames('power-icon',
                    item.action === EmailStepAction.ACTIVATE && 'active')}/>
                <label>{item.action === EmailStepAction.ACTIVATE ? 'Ingeschakeld' : 'Uitgeschakeld'} op</label>
                <label>{new IDate(item.date).format(DUTCH_DATE_TIME_FORMAT)}</label>
            </div>
        );
    }

    function renderInfoContainer() {
        if (!open) return null;
        return (
            <div className='email-info-container'>
                <CloseButton onClick={onClose} className='btn-close'/>
                <Loader active={loading}/>
                {renderEmailType()}
                {renderField('ID', emailStep?.id)}
                {renderField('Naam', emailStep?.label)}
                {renderField('Gemaakt op', emailStep?.createdAtMoment.format(DUTCH_DATE_TIME_FORMAT))}
                {renderField('Laatst gewijzigd', emailStep?.updatedAtMoment?.format(DUTCH_DATE_TIME_FORMAT))}
                {renderProducts()}
                {renderField('Status', emailStep?.active ? 'Actief' : 'Inactief')}
                {renderField('Actief sinds',
                    emailStep?.active ? emailStep.activeSinceMoment?.format(DUTCH_DATE_TIME_FORMAT) ?? '-' : '-')}
                {renderActivity()}
            </div>
        );
    }

    return (
        <div className={classNames('email-info', open && 'open')}>
            <AwesomeIcon name='info-circle' onClick={onActivate} className='info-icon'/>
            {renderInfoContainer()}
        </div>
    );
}

EmailAdminInfo.displayName = 'EmailAdminInfo';